<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import Icon from 'next/icon/Icon.vue';
import { Editor } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import SpaceTierSelect from './components/SpaceTierSelect.vue';

const props = defineProps({
  space: {
    type: Object,
    default: null,
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
  active: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['close', 'saved', 'cancel', 'submit']);

const store = useStore();
const { t } = useI18n();

const nameInputRef = ref(null);

const formData = ref({
  name: '',
  description: '',
  parent_id: null,
  venue_id: null,
  square_meters: null,
  standing: null,
  theater: null,
  classroom: null,
  banquet: null,
  boardroom: null,
  u_shape: null,
  max_capacity_seated: null,
  max_capacity_seated_furn: null,
  max_capacity_standing: null,
  tier: null,
});

const capacityValue = ref('');
const capacityType = ref('standing');

const CAPACITY_TYPE_OPTIONS = [
  {
    value: 'standing',
    labelKey: 'SETTINGS.SPACES.MAX_CAPACITY_STANDING',
    field: 'max_capacity_standing',
  },
  {
    value: 'seated_furn',
    labelKey: 'SETTINGS.SPACES.MAX_CAPACITY_SEATED_FURN',
    field: 'max_capacity_seated_furn',
  },
  {
    value: 'seated_free',
    labelKey: 'SETTINGS.SPACES.MAX_CAPACITY_SEATED_FREE',
    field: 'max_capacity_seated',
  },
];

const selectedParent = ref(null);
const selectedVenue = ref(null);
const selectedConfiguration = ref(null);

// TipTap Editor
const descriptionEditor = ref(null);

const activeConfigurations = ref([]);
const availableConfigurations = [
  {
    key: 'theater',
    label: 'SETTINGS.SPACES.THEATER',
    icon: 'i-lucide-presentation',
  },
  {
    key: 'classroom',
    label: 'SETTINGS.SPACES.CLASSROOM',
    icon: 'i-lucide-graduation-cap',
  },
  {
    key: 'banquet',
    label: 'SETTINGS.SPACES.BANQUET',
    icon: 'i-lucide-utensils',
  },
  {
    key: 'boardroom',
    label: 'SETTINGS.SPACES.BOARDROOM',
    icon: 'i-lucide-briefcase',
  },
  { key: 'u_shape', label: 'SETTINGS.SPACES.U_SHAPE', icon: 'i-lucide-move' },
  {
    key: 'standing',
    label: 'SETTINGS.SPACES.STANDING',
    icon: 'i-lucide-music',
  },
];

const isEditing = computed(() => !!props.space);
const canSubmit = computed(() => !!formData.value.name?.trim());
const uiFlags = computed(() => store.state.spaces.uiFlags);
const isSubmittingLocal = computed(
  () =>
    props.isSubmitting || uiFlags.value.isCreating || uiFlags.value.isUpdating
);

const venueOptions = computed(() =>
  (store.getters['venues/getVenues'] || []).map(venue => ({
    id: venue.id,
    name: venue.name,
    icon: 'i-lucide-building-2',
  }))
);

const remainingConfigurations = computed(() => {
  return availableConfigurations
    .filter(config => !activeConfigurations.value.includes(config.key))
    .map(config => ({
      id: config.key,
      name: t(config.label),
      icon: config.icon,
    }));
});

// Get all top-level spaces formatted for SingleSelect
const parentSpaceOptions = computed(() => {
  const spaces = store.getters['spaces/getSpaces'];
  return spaces
    .filter(s => {
      if (s.parent_id) return false;
      if (isEditing.value && s.id === props.space.id) return false;
      return true;
    })
    .map(s => ({
      id: s.id,
      name: s.name,
      icon: 'i-lucide-orbit',
    }));
});

const addConfiguration = configKey => {
  if (!activeConfigurations.value.includes(configKey)) {
    activeConfigurations.value.push(configKey);
  }
  selectedConfiguration.value = null;
};

const removeConfiguration = configKey => {
  activeConfigurations.value = activeConfigurations.value.filter(
    key => key !== configKey
  );
  formData.value[configKey] = null;
};

// Watch for parent selection changes
watch(selectedParent, newParent => {
  formData.value.parent_id = newParent?.id || null;
});

watch(selectedVenue, newVenue => {
  formData.value.venue_id = newVenue?.id || null;
});

// Watch for configuration selection
watch(selectedConfiguration, newConfig => {
  if (newConfig?.id) {
    addConfiguration(newConfig.id);
  }
});

// Get all spaces except current one (to prevent circular references)
const availableParentSpaces = computed(() => {
  const spaces = store.getters['spaces/getSpaces'];
  if (!isEditing.value) return spaces;

  // Filter out current space and its descendants
  return spaces.filter(s => {
    if (s.id === props.space.id) return false;
    // Check if this space is a descendant of current space
    let parent = spaces.find(p => p.id === s.parent_id);
    while (parent) {
      if (parent.id === props.space.id) return false;
      parent = spaces.find(p => p.id === parent.parent_id);
    }
    return true;
  });
});

watch(
  () => props.space,
  newSpace => {
    if (newSpace) {
      formData.value = {
        name: newSpace.name || '',
        description: newSpace.description || '',
        parent_id: newSpace.parent_id || null,
        venue_id: newSpace.venue_id || null,
        square_meters: newSpace.square_meters || null,
        standing: newSpace.standing || null,
        theater: newSpace.theater || null,
        classroom: newSpace.classroom || null,
        banquet: newSpace.banquet || null,
        boardroom: newSpace.boardroom || null,
        u_shape: newSpace.u_shape || null,
        max_capacity_seated: newSpace.max_capacity_seated || null,
        max_capacity_seated_furn: newSpace.max_capacity_seated_furn || null,
        max_capacity_standing: newSpace.max_capacity_standing || null,
        tier: newSpace.tier || null,
      };

      // Prefill capacity type + value from existing max capacity fields
      if (newSpace.max_capacity_standing != null && newSpace.max_capacity_standing !== '') {
        capacityType.value = 'standing';
        capacityValue.value = newSpace.max_capacity_standing;
      } else if (
        newSpace.max_capacity_seated_furn != null &&
        newSpace.max_capacity_seated_furn !== ''
      ) {
        capacityType.value = 'seated_furn';
        capacityValue.value = newSpace.max_capacity_seated_furn;
      } else if (
        newSpace.max_capacity_seated != null &&
        newSpace.max_capacity_seated !== ''
      ) {
        capacityType.value = 'seated_free';
        capacityValue.value = newSpace.max_capacity_seated;
      } else {
        capacityType.value = 'standing';
        capacityValue.value = '';
      }

      // Set active configurations based on existing values
      activeConfigurations.value = [];
      [
        'theater',
        'classroom',
        'banquet',
        'boardroom',
        'u_shape',
        'standing',
      ].forEach(key => {
        if (newSpace[key] != null) {
          activeConfigurations.value.push(key);
        }
      });

      // Set parent selection
      if (newSpace.parent_id) {
        const spaces = store.getters['spaces/getSpaces'];
        const parent = spaces.find(s => s.id === newSpace.parent_id);
        selectedParent.value = parent
          ? { id: parent.id, name: parent.name }
          : null;
      } else {
        selectedParent.value = null;
      }

      // Set venue selection
      if (newSpace.venue_id) {
        const venues = store.getters['venues/getVenues'] || [];
        const venue = venues.find(v => v.id === newSpace.venue_id);
        selectedVenue.value = venue
          ? { id: venue.id, name: venue.name, icon: 'i-lucide-building-2' }
          : null;
      } else {
        selectedVenue.value = null;
      }
    } else {
      formData.value = {
        name: '',
        description: '',
        parent_id: null,
        venue_id: null,
        square_meters: null,
        standing: null,
        theater: null,
        classroom: null,
        banquet: null,
        boardroom: null,
        u_shape: null,
        max_capacity_seated: null,
        max_capacity_seated_furn: null,
        max_capacity_standing: null,
        tier: null,
      };
      capacityType.value = 'standing';
      capacityValue.value = '';
      activeConfigurations.value = [];
      selectedParent.value = null;
      selectedVenue.value = null;
    }
  },
  { immediate: true }
);

const handleEscape = e => {
  if (props.active && e.key === 'Escape') {
    emit('close');
  }
};

const focusNameInput = () => {
  nextTick(() => nameInputRef.value?.focus());
};

// Initialize TipTap editor when component mounts
onMounted(() => {
  document.addEventListener('keydown', handleEscape);
  store.dispatch('venues/get');
  descriptionEditor.value = new Editor({
    content: formData.value.description || '<p></p>',
    editable: true,
    extensions: [StarterKit],
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none min-h-[120px] p-3 focus:outline-none dark:prose-invert',
      },
    },
    onUpdate: ({ editor }) => {
      formData.value.description = editor.getHTML();
    },
  });
  focusNameInput();
});

// Update editor content when formData.description changes externally
watch(
  () => formData.value.description,
  newDescription => {
    if (
      descriptionEditor.value &&
      descriptionEditor.value.getHTML() !== newDescription
    ) {
      descriptionEditor.value.commands.setContent(
        newDescription || '<p></p>',
        false
      );
    }
  }
);

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleEscape);
  if (descriptionEditor.value) {
    descriptionEditor.value.destroy();
  }
});

const handleSubmit = async () => {
  // Clean up numeric fields - convert empty strings and invalid numbers to null
  const cleanNumeric = value => {
    if (
      value === '' ||
      value === null ||
      value === undefined ||
      Number.isNaN(value)
    ) {
      return null;
    }
    return Number(value);
  };

  if (!canSubmit.value) return;

  const cleanedData = {
    ...formData.value,
    square_meters: cleanNumeric(formData.value.square_meters),
    standing: cleanNumeric(formData.value.standing),
    theater: cleanNumeric(formData.value.theater),
    classroom: cleanNumeric(formData.value.classroom),
    banquet: cleanNumeric(formData.value.banquet),
    boardroom: cleanNumeric(formData.value.boardroom),
    u_shape: cleanNumeric(formData.value.u_shape),
    max_capacity_seated: null,
    max_capacity_seated_furn: null,
    max_capacity_standing: null,
  };

  const capacityOption = CAPACITY_TYPE_OPTIONS.find(
    option => option.value === capacityType.value
  );
  const capacityField = capacityOption?.field || 'max_capacity_standing';
  const capacityNum = cleanNumeric(capacityValue.value);
  if (capacityNum != null && capacityNum > 0) {
    cleanedData[capacityField] = capacityNum;
  }

  // Emit the data for parent to handle
  emit('submit', cleanedData);
};

const handleClose = () => {
  emit('close');
};

const resetForm = () => {
  formData.value = {
    name: '',
    description: '',
    parent_id: null,
    venue_id: null,
    square_meters: null,
    standing: null,
    theater: null,
    classroom: null,
    banquet: null,
    boardroom: null,
    u_shape: null,
    max_capacity_seated: null,
    max_capacity_seated_furn: null,
    max_capacity_standing: null,
    tier: null,
  };
  capacityType.value = 'standing';
  capacityValue.value = '';
  activeConfigurations.value = [];
  selectedParent.value = null;
  selectedVenue.value = null;
  if (descriptionEditor.value) {
    descriptionEditor.value.commands.setContent('<p></p>', false);
  }
};

const onCancel = () => {
  resetForm();
  emit('cancel');
};

defineExpose({ resetForm });
</script>

<template>
  <form class="space-y-4" @submit.prevent="handleSubmit">
    <!-- Name Field -->
    <div>
      <label
        class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
        for="space-name-inline"
      >
        {{ t('SETTINGS.SPACES.NAME') }}
        <span class="text-red-500">*</span>
      </label>
      <input
        id="space-name-inline"
        ref="nameInputRef"
        v-model="formData.name"
        type="text"
        required
        class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 !text-slate-900 d"
        :placeholder="t('SETTINGS.SPACES.NAME_PLACEHOLDER')"
      />
    </div>

    <!-- Capacity (recommended) -->
    <div>
      <label
        class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
        for="space-capacity-inline"
      >
        {{ t('SETTINGS.SPACES.MAX_CAPACITY') }}
      </label>
      <div class="grid grid-cols-2 gap-2">
        <input
          id="space-capacity-inline"
          v-model="capacityValue"
          type="number"
          min="1"
          step="1"
          class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 !text-slate-900"
          :placeholder="t('SETTINGS.SPACES.CAPACITY_PLACEHOLDER')"
        />
        <select
          v-model="capacityType"
          class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 !text-slate-900"
          :aria-label="t('SETTINGS.SPACES.CAPACITY_TYPE')"
        >
          <option
            v-for="option in CAPACITY_TYPE_OPTIONS"
            :key="option.value"
            :value="option.value"
          >
            {{ t(option.labelKey) }}
          </option>
        </select>
      </div>
      <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
        {{ t('SETTINGS.SPACES.MAX_CAPACITY_HINT') }}
      </p>
    </div>

    <!-- Tier (recommended for suggestions) -->
    <div>
      <label
        class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
      >
        {{ t('SETTINGS.SPACES.TIER_LABEL') }}
      </label>
      <SpaceTierSelect v-model="formData.tier" />
      <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
        {{ t('SETTINGS.SPACES.TIER_HINT') }}
      </p>
    </div>

    <!-- Venue + Parent Space -->
    <div class="grid grid-cols-2 gap-3">
      <div>
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
        >
          {{ t('SETTINGS.SPACES.ASSIGN_VENUE') }}
        </label>
        <SingleSelect
          v-model="selectedVenue"
          :options="venueOptions"
          :placeholder="t('SETTINGS.SPACES.ASSIGN_VENUE')"
          :search-placeholder="t('SETTINGS.SPACES.VENUES.NAME_PLACEHOLDER')"
        />
        <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {{ t('SETTINGS.SPACES.VENUE_HINT') }}
        </p>
      </div>
      <div>
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
        >
          {{ t('SETTINGS.SPACES.PARENT_SPACE') }}
        </label>
        <div class="flex items-center gap-2">
          <SingleSelect
            v-model="selectedParent"
            :options="parentSpaceOptions"
            :placeholder="t('SETTINGS.SPACES.NO_PARENT')"
            :search-placeholder="t('SETTINGS.SPACES.SEARCH_PARENT')"
          />
          <button
            v-if="selectedParent"
            type="button"
            class="p-2 text-slate-400 hover:text-slate-600 rounded-lg"
            :title="t('SETTINGS.SPACES.STRUCTURE.CLEAR_PARENT')"
            @click="selectedParent = null"
          >
            <Icon icon="i-lucide-x" class="size-4" />
          </button>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="flex items-center justify-end gap-2 pt-4">
      <button
        type="button"
        class="h-9 px-3 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-md hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
        @click="onCancel"
      >
        {{ t('PLANNING.EVENTS.CANCEL') }}
      </button>
      <button
        type="submit"
        class="h-9 px-3 text-sm font-medium text-white bg-woot-500 rounded-md hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        :disabled="isSubmittingLocal || !canSubmit"
      >
        {{
          isEditing ? t('SETTINGS.SPACES.UPDATE') : t('SETTINGS.SPACES.CREATE')
        }}
      </button>
    </div>
  </form>
</template>
